import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { Box, zIndex } from '@factorialco/deprecated-design-system'
import { F0Button, F0Icon } from '@factorialco/f0-react'
import { ChevronDown, ChevronUp } from '@factorialco/f0-react/icons/app'
import i18n from 'modules/core/lib/i18n'
import { TASK_STEP_COUNTS } from 'modules/get_started/lib/tours/timeTrackingTour'
import { TOUR_WIDGET_Z_INDEX } from 'modules/get_started/lib/tours/tourUtils'
import type { DemoTourTask } from 'modules/get_started/hooks/useDemoTourTasks'
import ProgressCircle from 'modules/get_started/components/DemoAiTours/ProgressCircle'

type TaskStatus = 'pending' | 'in_progress' | 'completed'

interface Props {
  tasks: DemoTourTask[]
  completedTasks: string[]
  activeTaskKey: string | null
  allCompleted: boolean
  currentStep: number
  onStartTask: (taskKey: string) => void
  onStopTask: () => void
  onBookMeeting: () => void
  onRestart: () => void
}

const StatusIcon = ({ status }: { status: TaskStatus }) => {
  if (status === 'completed') {
    return (
      <Box
        width="s20"
        height="s20"
        borderRadius={{ all: 'rel500' }}
        alignItems="center"
        justifyContent="center"
        flexNoShrink
        style={{ backgroundColor: '#16A34A' }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M2.5 6L5 8.5L9.5 3.5"
            stroke="#FFFFFF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    )
  }
  if (status === 'in_progress') {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="10" cy="10" r="8.5" stroke="#D1D5DB" strokeWidth="2" />
        <path
          d="M10 1.5A8.5 8.5 0 0 1 18.5 10"
          stroke="#374151"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="10" cy="10" r="2" fill="#374151" />
      </svg>
    )
  }
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="10" cy="10" r="8" stroke="#9CA3AF" strokeWidth="1.5" strokeDasharray="4 3" />
    </svg>
  )
}

const CompletedCheckIcon = () => (
  <Box
    width="s24"
    height="s24"
    borderRadius={{ all: 'rel500' }}
    alignItems="center"
    justifyContent="center"
    flexNoShrink
    style={{ backgroundColor: '#16A34A' }}
  >
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M3 7L6 10L11 4"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
)

const ProgressBar = ({
  completedTasks,
  activeTaskKey,
  currentStep,
  tasks,
}: {
  completedTasks: string[]
  activeTaskKey: string | null
  currentStep: number
  tasks: Array<{ taskKey: string }>
}) => {
  const totalSegments = tasks.reduce((sum, t) => sum + (TASK_STEP_COUNTS[t.taskKey] ?? 1), 0)
  const completedSet = new Set(completedTasks)
  let completedSegments = 0
  for (const t of tasks) {
    const count = TASK_STEP_COUNTS[t.taskKey] ?? 1
    if (completedSet.has(t.taskKey)) {
      completedSegments += count
    } else if (t.taskKey === activeTaskKey) {
      completedSegments += Math.min(currentStep + 1, count)
    }
  }
  const percentage =
    totalSegments > 0 ? Math.min((completedSegments / totalSegments) * 100, 100) : 0

  return (
    <div
      style={{
        width: '100%',
        height: '8px',
        backgroundColor: '#E5E7EB',
        borderRadius: '4px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${String(percentage)}%`,
          height: '100%',
          backgroundColor: '#06838B',
          borderRadius: '4px',
          transition: 'width 0.4s ease',
        }}
      />
    </div>
  )
}

const DemoTourPopover = ({
  tasks,
  completedTasks,
  activeTaskKey,
  allCompleted,
  currentStep,
  onStartTask,
  onStopTask,
  onBookMeeting,
  onRestart,
}: Props) => {
  const [expanded, setExpanded] = useState(false)

  const completedSet = new Set(completedTasks)
  const completedCount = tasks.filter((t) => completedSet.has(t.taskKey)).length
  const totalCount = tasks.length
  const activeTaskIndex = activeTaskKey ? tasks.findIndex((t) => t.taskKey === activeTaskKey) : -1
  const nextIncompleteTask = tasks.find(
    (t) => !completedSet.has(t.taskKey) && t.taskKey !== activeTaskKey
  )

  const getTaskStatus = (taskKey: string): TaskStatus => {
    if (completedSet.has(taskKey)) return 'completed'
    if (taskKey === activeTaskKey) return 'in_progress'
    return 'pending'
  }

  const handleTaskClick = (taskKey: string) => {
    const status = getTaskStatus(taskKey)
    if (status === 'in_progress') {
      onStopTask()
    } else {
      onStartTask(taskKey)
    }
  }

  const stepLabel = activeTaskKey
    ? `${String(activeTaskIndex + 1)}/${String(totalCount)}`
    : `${String(completedCount)}/${String(totalCount)}`
  const stepText = i18n.t('growth.get_started.demo_tour.popover_title')

  return createPortal(
    <Box
      gap="s8"
      style={{
        position: 'fixed',
        bottom: '32px',
        left: '32px',
        zIndex: activeTaskKey !== null ? TOUR_WIDGET_Z_INDEX : String(zIndex.popovers),
        width: '300px',
      }}
    >
      {expanded ? (
        <Box
          padding="s12"
          gap="s2"
          borderRadius={{ all: 'abs016' }}
          background="white"
          boxShadow="s200"
        >
          {tasks.map((task) => {
            const status = getTaskStatus(task.taskKey)
            return (
              <div
                key={task.taskKey}
                role="button"
                tabIndex={0}
                aria-label={task.title}
                onClick={() => handleTaskClick(task.taskKey)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleTaskClick(task.taskKey)
                  }
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: '40px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  gap: '10px',
                }}
                className="hover:bg-f1-background-tertiary transition-colors duration-150"
              >
                <StatusIcon status={status} />
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                    letterSpacing: '-0.07px',
                    color: status === 'completed' ? '#6B7280' : '#0D1625',
                    textDecoration: status === 'completed' ? 'line-through' : 'none',
                  }}
                >
                  {task.title}
                </span>
              </div>
            )
          })}
        </Box>
      ) : null}

      <Box
        borderRadius={{ all: 'abs016' }}
        background="white"
        boxShadow="s200"
        gap="s8"
        style={{ padding: '10px 12px' }}
      >
        <div
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded((e) => !e)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setExpanded((current) => !current)
            }
          }}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
          }}
        >
          {allCompleted ? (
            <CompletedCheckIcon />
          ) : (
            <ProgressCircle current={completedCount} total={totalCount} size={16} />
          )}
          <span
            style={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '-0.07px',
              color: '#0D1625',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {allCompleted
              ? i18n.t('growth.get_started.demo_tour.tour_complete_title')
              : `${stepLabel}\u00A0\u00A0${stepText}`}
          </span>
          <F0Icon icon={expanded ? ChevronUp : ChevronDown} size="sm" />
        </div>

        {allCompleted ? (
          <Box flexDirection="row" alignItems="center" gap="s8">
            <Box
              component="button"
              width="s32"
              height="s32"
              borderRadius={{ all: 'abs008' }}
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              flexNoShrink
              aria-label={i18n.t('growth.get_started.demo_tour.restart_tour')}
              onClick={onRestart}
              style={{ backgroundColor: 'transparent', border: 'none' }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M2.5 2.5V6H6"
                  stroke="#374151"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.05 10A5.5 5.5 0 1 0 4.34 4.34L2.5 6"
                  stroke="#374151"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            <Box
              flexNoShrink
              style={{ width: '1px', height: '20px', backgroundColor: '#E5E7EB' }}
            />
            <Box flexGrow>
              <F0Button
                variant="default"
                label={i18n.t('growth.get_started.demo_tour.book_meeting')}
                onClick={onBookMeeting}
                fullWidth
              />
            </Box>
          </Box>
        ) : (
          <Box flexDirection="row" alignItems="center" gap="s8">
            <Box flexGrow>
              <ProgressBar
                completedTasks={completedTasks}
                activeTaskKey={activeTaskKey}
                currentStep={currentStep}
                tasks={tasks}
              />
            </Box>
            {activeTaskKey !== null ? (
              <Box
                component="button"
                width="s32"
                height="s32"
                borderRadius={{ all: 'abs008' }}
                background="grey200"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                flexNoShrink
                aria-label={i18n.t('growth.get_started.demo_tour.stop_tour')}
                onClick={() => onStopTask()}
                style={{ border: 'none' }}
              >
                <div
                  style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#B91C1C' }}
                />
              </Box>
            ) : null}
            {activeTaskKey === null && nextIncompleteTask !== undefined ? (
              <Box
                component="button"
                width="s32"
                height="s32"
                borderRadius={{ all: 'abs008' }}
                background="grey200"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                flexNoShrink
                aria-label={i18n.t('growth.get_started.demo_tour.continue_tour')}
                onClick={() => onStartTask(nextIncompleteTask.taskKey)}
                style={{ border: 'none' }}
              >
                <span style={{ fontSize: 14, color: '#374151' }}>▶</span>
              </Box>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>,
    document.body
  )
}

export default DemoTourPopover
